import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MarkersAPI } from '../../../../api/MarkerEndpoints';
import { ArtistsAPI } from '../../../../api/ArtistsAPI';
import { ALL_GENRES, ALL_DRESSCODES, ALL_SPECIAL_TYPES, ALL_PRICES } from '../../../../constants/EventConstants';
import { eventImageSrc, instagramPostInfo } from '../../../../utils/eventImage';
import { WEEKDAYS, normalizeDay, unwrap, text as str, isEventMarker } from '../markerUtils';
import './MarkerEditor.css';

// One form for every field an event or club carries. Saving sends the
// marker as a draft (PUT) and approves it (PATCH) in one go: the backend
// only merges a draft into the live marker on approval, so a plain PUT from
// an admin would never show up in the app.

export const BOX_OFFICE = [
  ['', 'Keine Angabe'],
  ['UNLIMITED', 'Abendkassa unbegrenzt'],
  ['LIMITED', 'Abendkassa begrenzt'],
  ['PRESALE_ONLY', 'Nur Vorverkauf'],
  ['UNKNOWN', 'Unbekannt'],
];

const DESCRIPTION_LIMIT = 1000;

// ---- Value helpers ---------------------------------------------------------

const num = (value) => {
  const v = unwrap(value);
  if (v === '' || v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
};

// The import may store "9:00:00"; read with one or two hour digits, or the day would
// look closed here and be dropped on save.
const clock = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(str(value));
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
};

const toTime = (hhmm) => (hhmm ? `${hhmm}:00` : '');

const toNumberOrNull = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// ---- Form state ------------------------------------------------------------

export function makeForm(marker) {
  const rawDays = Array.isArray(unwrap(marker?.weekdays)) ? unwrap(marker.weekdays) : [];
  const byDay = new Map();
  rawDays.forEach((entry) => {
    const day = normalizeDay(entry?.day);
    if (day) byDay.set(day, { open: clock(entry?.open), close: clock(entry?.close) });
  });
  const artists = (Array.isArray(unwrap(marker?.artists)) ? unwrap(marker.artists) : [])
    .map((artist) => (artist && typeof artist === 'object'
      ? { name: str(artist.name), instagram: str(artist.instagram).replace(/^@/, ''), artistSpotifyId: str(artist.artistSpotifyId) }
      : { name: str(artist), instagram: '', artistSpotifyId: '' }))
    .filter((artist) => artist.name)
    // Each act carries a key of its own, so a Spotify lookup still finds its act after
    // another one above it was removed.
    .map((artist, index) => ({ key: `saved-${index}`, ...artist }));
  const genres = Array.isArray(unwrap(marker?.musicGenre)) ? unwrap(marker.musicGenre).map(str).filter(Boolean) : [];

  return {
    title: str(marker?.title),
    descriptionGerman: str(marker?.descriptionGerman),
    descriptionEnglish: str(marker?.descriptionEnglish),
    musicGenre: genres,
    image: str(marker?.image),
    instagramName: str(marker?.instagramName).replace(/^@/, ''),
    address: str(marker?.address),
    latitude: num(marker?.latitude),
    longitude: num(marker?.longitude),
    specialType: str(marker?.specialType),
    dressCode: str(marker?.dressCode),
    drinkPriceIndicator: num(marker?.drinkPriceIndicator),
    entryPrice: num(marker?.entryPrice),
    minAge: num(marker?.minAge),
    femalePercentage: num(marker?.femalePercentage),
    malePercentage: num(marker?.malePercentage),
    diversPercentage: num(marker?.diversPercentage),
    studentPercentage: num(marker?.studentPercentage),
    // Event
    eventDate: str(marker?.eventDate).slice(0, 10),
    startTime: clock(marker?.startTime),
    endTime: clock(marker?.endTime),
    ticketUrl: str(marker?.ticketUrl),
    boxOfficePrice: num(marker?.boxOfficePrice),
    boxOfficeAvailability: str(marker?.boxOfficeAvailability),
    hasAwarenessTeam: unwrap(marker?.hasAwarenessTeam) === true,
    artists,
    // Club
    weekdays: WEEKDAYS.map(({ value }) => ({
      day: value,
      enabled: byDay.has(value) && Boolean(byDay.get(value).open && byDay.get(value).close),
      open: byDay.get(value)?.open ?? '',
      close: byDay.get(value)?.close ?? '',
    })),
  };
}

export function validateForm(form, event) {
  const errors = {};
  if (!form.title.trim()) errors.title = 'Titel ist Pflicht.';
  const lat = Number(form.latitude);
  const lon = Number(form.longitude);
  if (form.latitude === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) errors.latitude = 'Breitengrad zwischen -90 und 90.';
  if (form.longitude === '' || !Number.isFinite(lon) || lon < -180 || lon > 180) errors.longitude = 'Längengrad zwischen -180 und 180.';
  if (form.image.trim() && !/^(https?:\/\/|data:image\/)/i.test(form.image.trim())) errors.image = 'Instagram-Post-URL, Bild-URL oder Data-URL.';
  if (form.ticketUrl.trim()) {
    try { new URL(form.ticketUrl.trim()); } catch { errors.ticketUrl = 'Keine gültige URL.'; }
  }
  for (const key of ['femalePercentage', 'malePercentage', 'diversPercentage']) {
    if (form[key] !== '' && (Number(form[key]) < 0 || Number(form[key]) > 100)) errors[key] = '0 bis 100.';
  }
  if (form.studentPercentage !== '' && (Number(form.studentPercentage) < 0 || Number(form.studentPercentage) > 1)) errors.studentPercentage = 'Anteil 0 bis 1, z. B. 0.35.';
  if (form.minAge !== '' && (Number(form.minAge) < 0 || Number(form.minAge) > 99)) errors.minAge = '0 bis 99.';
  if (form.entryPrice !== '' && Number(form.entryPrice) < 0) errors.entryPrice = 'Nicht negativ.';
  if (event) {
    if (!form.eventDate) errors.eventDate = 'Datum ist Pflicht.';
    if (!form.startTime) errors.startTime = 'Beginn ist Pflicht.';
    if (!form.endTime) errors.endTime = 'Ende ist Pflicht.';
    if (form.boxOfficePrice !== '' && Number(form.boxOfficePrice) < 0) errors.boxOfficePrice = 'Nicht negativ.';
  } else {
    form.weekdays.forEach((day) => {
      if (day.enabled && (!day.open || !day.close)) errors[`weekday.${day.day}`] = 'Öffnet und schließt angeben.';
    });
  }
  return errors;
}

export function buildPayload(form, marker) {
  const event = isEventMarker(marker);
  const payload = {
    id: Number(marker.id),
    markerType: event ? 'Event' : 'Club',
    title: form.title.trim(),
    descriptionGerman: form.descriptionGerman.trim(),
    descriptionEnglish: form.descriptionEnglish.trim(),
    musicGenre: form.musicGenre,
    image: form.image.trim(),
    instagramName: form.instagramName.trim().replace(/^@/, ''),
    address: form.address.trim(),
    latitude: Number(form.latitude),
    longitude: Number(form.longitude),
    specialType: form.specialType || null,
    dressCode: form.dressCode || null,
    drinkPriceIndicator: toNumberOrNull(form.drinkPriceIndicator),
    entryPrice: toNumberOrNull(form.entryPrice),
    minAge: toNumberOrNull(form.minAge),
    femalePercentage: toNumberOrNull(form.femalePercentage),
    malePercentage: toNumberOrNull(form.malePercentage),
    diversPercentage: toNumberOrNull(form.diversPercentage),
    studentPercentage: toNumberOrNull(form.studentPercentage),
    averageAge: toNumberOrNull(unwrap(marker.averageAge)),
  };
  if (event) {
    return {
      ...payload,
      eventDate: form.eventDate,
      startTime: toTime(form.startTime),
      endTime: toTime(form.endTime),
      ticketUrl: form.ticketUrl.trim() || null,
      boxOfficePrice: toNumberOrNull(form.boxOfficePrice),
      boxOfficeAvailability: form.boxOfficeAvailability || null,
      hasAwarenessTeam: Boolean(form.hasAwarenessTeam),
      artists: form.artists
        .filter((artist) => artist.name.trim())
        .map((artist) => ({
          name: artist.name.trim(),
          instagram: artist.instagram.trim().replace(/^@/, '') || null,
          artistSpotifyId: artist.artistSpotifyId.trim() || null,
        })),
    };
  }
  return {
    ...payload,
    weekdays: form.weekdays
      .filter((day) => day.enabled && day.open && day.close)
      .map((day) => ({ day: day.day, open: toTime(day.open), close: toTime(day.close) })),
  };
}

// ---- Small UI pieces -------------------------------------------------------

const Icon = ({ path, size = 13 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" width={size} height={size} aria-hidden="true">{path}</svg>
);

const icons = {
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></>,
  text: <><path d="M4 6h16M4 12h16M4 18h10" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  pin: <><path d="M12 22s7-7.1 7-12a7 7 0 1 0-14 0c0 4.9 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></>,
  music: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="M20 6 9 17l-5-5" />,
  spotify: <><circle cx="12" cy="12" r="9" /><path d="M8 10c3-1 6-.6 8.5 1M8.5 13c2.5-.7 4.7-.4 6.5.7M9 16c2-.5 3.5-.3 5 .5" /></>,
};

function Section({ id, icon, title, hint, children }) {
  return (
    <section className="me-section" id={id} aria-labelledby={`${id}-title`}>
      <header className="me-section-head">
        <h3 id={`${id}-title`}><Icon path={icon} /> {title}</h3>
        {hint && <span className="me-section-hint">{hint}</span>}
      </header>
      <div className="me-grid">{children}</div>
    </section>
  );
}

function Field({ label, error, hint, wide, children, htmlFor }) {
  return (
    <div className={`me-field${wide ? ' me-field--wide' : ''}${error ? ' me-field--error' : ''}`}>
      <label className="me-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="me-error" role="alert">{error}</span> : hint ? <span className="me-hint">{hint}</span> : null}
    </div>
  );
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function MapPreview({ latitude, longitude }) {
  const lat = useDebounced(latitude === '' ? NaN : Number(latitude), 500);
  const lon = useDebounced(longitude === '' ? NaN : Number(longitude), 500);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const dx = 0.0045;
  const dy = 0.0032;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${lon - dx}%2C${lat - dy}%2C${lon + dx}%2C${lat + dy}&layer=mapnik&marker=${lat}%2C${lon}`;
  return (
    <div className="me-map">
      <iframe title="Karte" src={src} loading="lazy" />
      <div className="me-map-foot">
        <span>{lat.toFixed(5)}, {lon.toFixed(5)}</span>
        <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`} target="_blank" rel="noreferrer">OpenStreetMap</a>
      </div>
    </div>
  );
}

// ---- Editor ----------------------------------------------------------------

export default function MarkerEditor({ marker, onSaved, onCancel, onDirtyChange }) {
  const event = isEventMarker(marker);
  const [form, setForm] = useState(() => makeForm(marker));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [spotifyState, setSpotifyState] = useState({});
  const spotifyTimers = useRef({});
  const nextArtistKey = useRef(0);
  const initial = useMemo(() => JSON.stringify(makeForm(marker)), [marker]);
  const dirty = JSON.stringify(form) !== initial;

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => Object.values(spotifyTimers.current).forEach(clearTimeout), []);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setInput = (key) => (e) => set(key)(e.target.value);
  const setChecked = (key) => (e) => set(key)(e.target.checked);

  const toggleGenre = (genre) => setForm((prev) => ({
    ...prev,
    musicGenre: prev.musicGenre.includes(genre) ? prev.musicGenre.filter((g) => g !== genre) : [...prev.musicGenre, genre],
  }));

  const setArtist = (artistKey, key, value) => setForm((prev) => ({
    ...prev,
    artists: prev.artists.map((artist) => (artist.key === artistKey ? { ...artist, [key]: value } : artist)),
  }));
  const addArtist = () => setForm((prev) => ({
    ...prev,
    artists: [...prev.artists, { key: `new-${nextArtistKey.current++}`, name: '', instagram: '', artistSpotifyId: '' }],
  }));
  const removeArtist = (artistKey) => {
    clearTimeout(spotifyTimers.current[artistKey]);
    delete spotifyTimers.current[artistKey];
    setSpotifyState((s) => ({ ...s, [artistKey]: undefined }));
    setForm((prev) => ({ ...prev, artists: prev.artists.filter((artist) => artist.key !== artistKey) }));
  };

  const lookupSpotify = (artistKey, name) => {
    clearTimeout(spotifyTimers.current[artistKey]);
    if (name.trim().length < 2) { setSpotifyState((s) => ({ ...s, [artistKey]: undefined })); return; }
    setSpotifyState((s) => ({ ...s, [artistKey]: 'loading' }));
    const timer = setTimeout(async () => {
      let found = null;
      try {
        found = (await ArtistsAPI.searchSpotify(name.trim()))?.spotifyId || null;
      } catch {
        found = null;
      }
      // An answer for an act that was removed, or whose name was typed on meanwhile, is dropped.
      if (spotifyTimers.current[artistKey] !== timer) return;
      delete spotifyTimers.current[artistKey];
      if (found) setArtist(artistKey, 'artistSpotifyId', found);
      setSpotifyState((s) => ({ ...s, [artistKey]: found ? 'found' : 'none' }));
    }, 600);
    spotifyTimers.current[artistKey] = timer;
  };

  const setWeekday = (dayValue, patch) => setForm((prev) => ({
    ...prev,
    weekdays: prev.weekdays.map((day) => (day.day === dayValue ? { ...day, ...patch } : day)),
  }));

  const save = async () => {
    const found = validateForm(form, event);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setSaveError('Bitte die markierten Felder prüfen.');
      document.querySelector('.me-field--error, .me-weekday--error')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = buildPayload(form, marker);
      await MarkersAPI.updateMarker(marker.id, payload);
      await MarkersAPI.approveMarker(marker.id);
      let saved = null;
      try { saved = await MarkersAPI.getMarker(marker.id); } catch { saved = null; }
      onSaved?.(saved && typeof saved === 'object' ? saved : { ...marker, ...payload, adminAccepted: true });
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Unbekannter Fehler';
      setSaveError(`Speichern fehlgeschlagen (${err?.response?.status ?? '?'}): ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const imagePreview = eventImageSrc(form.image);
  const post = instagramPostInfo(form.image);
  const openDays = form.weekdays.filter((d) => d.enabled).map((d) => WEEKDAYS.find((w) => w.value === d.day).short).join(' ');

  return (
    <form className="me" onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
      <div className="me-columns">
        <div className="me-main">
          <Section id="me-basics" icon={icons.info} title="Basis">
            <Field label="Titel *" error={errors.title} wide htmlFor="me-title">
              <input id="me-title" className="me-input" value={form.title} onChange={setInput('title')} maxLength={255} />
            </Field>
            <Field label="Instagram-Account" htmlFor="me-instagram" hint="ohne @">
              <div className="me-prefixed"><span>@</span>
                <input id="me-instagram" className="me-input" value={form.instagramName} onChange={setInput('instagramName')} placeholder="schick.vienna" />
              </div>
            </Field>
            <Field label="Kategorie" htmlFor="me-special">
              <select id="me-special" className="me-input" value={form.specialType} onChange={setInput('specialType')}>
                <option value="">— keine —</option>
                {ALL_SPECIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                {form.specialType && !ALL_SPECIAL_TYPES.some((t) => t.value === form.specialType) && (
                  <option value={form.specialType}>{form.specialType} (nicht in der Liste)</option>
                )}
              </select>
            </Field>
            <Field label="Bild" error={errors.image} wide htmlFor="me-image"
              hint="Instagram-Post-URL oder Bild-URL. Die Vorschau rechts zeigt, was die App zeigt.">
              <input id="me-image" className="me-input" value={form.image} onChange={setInput('image')} placeholder="https://www.instagram.com/p/…" />
            </Field>
          </Section>

          <Section id="me-text" icon={icons.text} title="Beschreibung" hint={`max. ${DESCRIPTION_LIMIT} Zeichen je Sprache`}>
            <Field label={`Deutsch (${form.descriptionGerman.length}/${DESCRIPTION_LIMIT})`} wide htmlFor="me-de">
              <textarea id="me-de" className="me-input me-textarea" value={form.descriptionGerman} onChange={setInput('descriptionGerman')} maxLength={DESCRIPTION_LIMIT} rows={3} />
            </Field>
            <Field label={`English (${form.descriptionEnglish.length}/${DESCRIPTION_LIMIT})`} wide htmlFor="me-en">
              <textarea id="me-en" className="me-input me-textarea" value={form.descriptionEnglish} onChange={setInput('descriptionEnglish')} maxLength={DESCRIPTION_LIMIT} rows={3} />
            </Field>
          </Section>

          {event ? (
            <Section id="me-when" icon={icons.calendar} title="Termin">
              <Field label="Datum *" error={errors.eventDate} htmlFor="me-date">
                <input id="me-date" className="me-input" type="date" value={form.eventDate} onChange={setInput('eventDate')} />
              </Field>
              <Field label="Beginn *" error={errors.startTime} htmlFor="me-start">
                <input id="me-start" className="me-input" type="time" value={form.startTime} onChange={setInput('startTime')} />
              </Field>
              <Field label="Ende *" error={errors.endTime} htmlFor="me-end" hint="Liegt das Ende vor dem Beginn, endet das Event am Folgetag.">
                <input id="me-end" className="me-input" type="time" value={form.endTime} onChange={setInput('endTime')} />
              </Field>
            </Section>
          ) : (
            <Section id="me-hours" icon={icons.clock} title="Öffnungszeiten" hint="Tage ohne Haken gelten als geschlossen">
              <div className="me-weekdays me-field--wide">
                {form.weekdays.map((day) => {
                  const meta = WEEKDAYS.find((d) => d.value === day.day);
                  const error = errors[`weekday.${day.day}`];
                  return (
                    <div key={day.day} className={`me-weekday${day.enabled ? ' me-weekday--on' : ''}${error ? ' me-weekday--error' : ''}`}>
                      <label className="me-weekday-toggle">
                        <input type="checkbox" checked={day.enabled} onChange={(e) => setWeekday(day.day, { enabled: e.target.checked })} />
                        <span>{meta.label}</span>
                      </label>
                      <input className="me-input" type="time" aria-label={`${meta.label} öffnet`} value={day.open} disabled={!day.enabled}
                        onChange={(e) => setWeekday(day.day, { open: e.target.value })} />
                      <span className="me-weekday-dash">–</span>
                      <input className="me-input" type="time" aria-label={`${meta.label} schließt`} value={day.close} disabled={!day.enabled}
                        onChange={(e) => setWeekday(day.day, { close: e.target.value })} />
                      {error && <span className="me-error" role="alert">{error}</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          <Section id="me-where" icon={icons.pin} title="Ort" hint="Adresse wie in der App: Straße Nr, PLZ Wien">
            <Field label="Adresse" wide htmlFor="me-address">
              <input id="me-address" className="me-input" value={form.address} onChange={setInput('address')} placeholder="Spittelberggasse 10, 1070 Wien" />
            </Field>
            <Field label="Breitengrad *" error={errors.latitude} htmlFor="me-lat">
              <input id="me-lat" className="me-input" type="number" step="any" inputMode="decimal" value={form.latitude} onChange={setInput('latitude')} placeholder="48.2082" />
            </Field>
            <Field label="Längengrad *" error={errors.longitude} htmlFor="me-lon">
              <input id="me-lon" className="me-input" type="number" step="any" inputMode="decimal" value={form.longitude} onChange={setInput('longitude')} placeholder="16.3738" />
            </Field>
          </Section>

          <Section id="me-music" icon={icons.music} title={event ? 'Musik & Line-up' : 'Musik'}>
            <div className="me-field me-field--wide">
              <span className="me-label">Genres</span>
              <div className="me-chips" role="group" aria-label="Genres">
                {/* Genres an import stored outside the list stay visible, so they can be removed. */}
                {[...ALL_GENRES, ...form.musicGenre.filter((genre) => !ALL_GENRES.includes(genre))].map((genre) => (
                  <button key={genre} type="button" className={`me-chip${form.musicGenre.includes(genre) ? ' me-chip--on' : ''}`}
                    aria-pressed={form.musicGenre.includes(genre)} onClick={() => toggleGenre(genre)}>{genre}</button>
                ))}
              </div>
            </div>
            {event && (
              <div className="me-field me-field--wide">
                <span className="me-label">Line-up</span>
                <div className="me-artists">
                  {form.artists.length === 0 && <span className="me-hint">Noch keine Acts eingetragen.</span>}
                  {form.artists.map((artist, index) => (
                    <div key={artist.key} className="me-artist">
                      <input className="me-input" placeholder="Name" aria-label={`Act ${index + 1} Name`} value={artist.name}
                        onChange={(e) => { setArtist(artist.key, 'name', e.target.value); lookupSpotify(artist.key, e.target.value); }} />
                      <div className="me-prefixed"><span>@</span>
                        <input className="me-input" placeholder="instagram" aria-label={`Act ${index + 1} Instagram`} value={artist.instagram}
                          onChange={(e) => setArtist(artist.key, 'instagram', e.target.value)} />
                      </div>
                      <div className={`me-spotify me-spotify--${spotifyState[artist.key] ?? 'idle'}`}
                        title={spotifyState[artist.key] === 'found' ? 'Spotify-ID gefunden' : spotifyState[artist.key] === 'none' ? 'Kein Spotify-Treffer' : ''}>
                        <Icon path={icons.spotify} size={13} />
                        <input className="me-input" placeholder="Spotify-ID" aria-label={`Act ${index + 1} Spotify-ID`} value={artist.artistSpotifyId}
                          onChange={(e) => setArtist(artist.key, 'artistSpotifyId', e.target.value)} />
                      </div>
                      <button type="button" className="me-icon-btn" aria-label={`Act ${index + 1} entfernen`} onClick={() => removeArtist(artist.key)}>
                        <Icon path={icons.x} size={12} />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="me-add" onClick={addArtist}><Icon path={icons.plus} size={12} /> Act hinzufügen</button>
                </div>
              </div>
            )}
          </Section>

          <Section id="me-crowd" icon={icons.users} title="Preise & Publikum">
            <Field label="Eintritt (€)" error={errors.entryPrice} htmlFor="me-price" hint="0 = gratis">
              <input id="me-price" className="me-input" type="number" min="0" step="0.5" inputMode="decimal" value={form.entryPrice} onChange={setInput('entryPrice')} />
            </Field>
            <Field label="Mindestalter" error={errors.minAge} htmlFor="me-age">
              <input id="me-age" className="me-input" type="number" min="0" max="99" inputMode="numeric" value={form.minAge} onChange={setInput('minAge')} />
            </Field>
            <Field label="Dresscode" htmlFor="me-dress">
              <select id="me-dress" className="me-input" value={form.dressCode} onChange={setInput('dressCode')}>
                <option value="">— keiner —</option>
                {ALL_DRESSCODES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
            <div className="me-field">
              <span className="me-label">Getränkepreise</span>
              <div className="me-segment" role="group" aria-label="Getränkepreise">
                {ALL_PRICES.map((p) => (
                  <button key={p} type="button" className={`me-segment-btn${String(form.drinkPriceIndicator) === String(p) ? ' me-segment-btn--on' : ''}`}
                    aria-pressed={String(form.drinkPriceIndicator) === String(p)} aria-label={`Getränkepreise ${p} von 3`}
                    onClick={() => set('drinkPriceIndicator')(String(form.drinkPriceIndicator) === String(p) ? '' : String(p))}>
                    {'€'.repeat(p)}
                  </button>
                ))}
              </div>
            </div>
            {event && (
              <>
                <Field label="Abendkassa (€)" error={errors.boxOfficePrice} htmlFor="me-box">
                  <input id="me-box" className="me-input" type="number" min="0" step="0.5" inputMode="decimal" value={form.boxOfficePrice} onChange={setInput('boxOfficePrice')} />
                </Field>
                <Field label="Abendkassa-Verfügbarkeit" htmlFor="me-avail">
                  <select id="me-avail" className="me-input" value={form.boxOfficeAvailability} onChange={setInput('boxOfficeAvailability')}>
                    {BOX_OFFICE.map(([value, label]) => <option key={value || 'none'} value={value}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Ticket-Link" error={errors.ticketUrl} wide htmlFor="me-ticket">
                  <input id="me-ticket" className="me-input" type="url" value={form.ticketUrl} onChange={setInput('ticketUrl')} placeholder="https://…" />
                </Field>
                <label className="me-switch me-field--wide">
                  <input type="checkbox" checked={form.hasAwarenessTeam} onChange={setChecked('hasAwarenessTeam')} />
                  <span>Awareness-Team vor Ort</span>
                </label>
              </>
            )}
            <Field label="Weiblich %" error={errors.femalePercentage} htmlFor="me-female">
              <input id="me-female" className="me-input" type="number" min="0" max="100" value={form.femalePercentage} onChange={setInput('femalePercentage')} />
            </Field>
            <Field label="Männlich %" error={errors.malePercentage} htmlFor="me-male">
              <input id="me-male" className="me-input" type="number" min="0" max="100" value={form.malePercentage} onChange={setInput('malePercentage')} />
            </Field>
            <Field label="Divers %" error={errors.diversPercentage} htmlFor="me-divers">
              <input id="me-divers" className="me-input" type="number" min="0" max="100" value={form.diversPercentage} onChange={setInput('diversPercentage')} />
            </Field>
            <Field label="Studierende (Anteil 0–1)" error={errors.studentPercentage} htmlFor="me-students" hint="0.35 = 35 %">
              <input id="me-students" className="me-input" type="number" min="0" max="1" step="0.05" value={form.studentPercentage} onChange={setInput('studentPercentage')} />
            </Field>
          </Section>
        </div>

        <aside className="me-side">
          <div className="me-preview">
            <div className="me-preview-media">
              {imagePreview
                ? <img src={imagePreview} alt="Bildvorschau" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <span className="me-hint">Kein Bild</span>}
            </div>
            <div className="me-preview-body">
              <strong>{form.title || 'Ohne Titel'}</strong>
              <span className="me-hint">
                {event
                  ? `${form.eventDate || 'Kein Datum'} · ${form.startTime || '?'} – ${form.endTime || '?'}`
                  : openDays || 'Keine Öffnungstage'}
              </span>
              <span className="me-hint">{form.address || 'Keine Adresse'}</span>
              {post && <a className="me-link" href={`https://www.instagram.com/${post.type}/${post.code}/${post.imgIndex > 1 ? `?img_index=${post.imgIndex}` : ''}`} target="_blank" rel="noreferrer">Instagram-Post öffnen</a>}
            </div>
          </div>

          <MapPreview latitude={form.latitude} longitude={form.longitude} />

          <dl className="me-meta">
            <dt>Status</dt><dd>{unwrap(marker.adminAccepted) ? 'Live in der App' : 'Nicht freigegeben'}</dd>
            {str(marker.createdAt) && <><dt>Angelegt</dt><dd>{new Date(str(marker.createdAt)).toLocaleDateString('de-AT')}</dd></>}
            {str(marker.clickCount) && <><dt>Aufrufe</dt><dd>{str(marker.clickCount)}</dd></>}
            {str(marker.amountUsersComing) && <><dt>Zusagen</dt><dd>{str(marker.amountUsersComing)}</dd></>}
            {!event && str(marker.rating) && <><dt>Bewertung</dt><dd>{str(marker.rating)} ({str(marker.amountRates) || 0})</dd></>}
            {str(marker.ageGroup) && <><dt>Altersgruppe</dt><dd>{str(marker.ageGroup)}</dd></>}
          </dl>

          <details className="me-raw">
            <summary>Rohdaten (JSON)</summary>
            <pre>{JSON.stringify(marker, null, 2)}</pre>
          </details>

          <p className="me-note">Speichern übernimmt alle Änderungen sofort in die App.</p>
        </aside>
      </div>

      <footer className="me-footer">
        {saveError && <span className="me-save-error" role="alert">{saveError}</span>}
        <span className="me-footer-spacer" />
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>Schließen</button>
        <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
          <Icon path={icons.check} size={13} /> {saving ? 'Speichere…' : 'Speichern'}
        </button>
      </footer>
    </form>
  );
}
